import React, { useEffect, useState } from 'react';

import { Formik, FormikConfig } from 'formik';
import { ChevronDown, Loader2 } from 'lucide-react';

import { cn } from 'components/lib/utils';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'components/ui/collapsible';

export interface CollapsibleSectionFormProps<T> {
  title: string;
  description?: string;
  defaultExpanded?: boolean;
  initialValues: T;
  validationSchema?: FormikConfig<T>['validationSchema'];
  onSubmit: FormikConfig<T>['onSubmit'];
  children: React.ReactNode;
  isLoading?: boolean;
  saveButtonText?: string;
  disableFormGrid?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  enableReinitialize?: boolean;
  isExternallyDirty?: boolean;
}

const FormDirtySync = ({
  dirty,
  onDirtyChange
}: {
  dirty: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  return null;
};

export function CollapsibleSectionForm<T extends Record<string, unknown>>({
  title,
  description = '',
  defaultExpanded = false,
  initialValues,
  validationSchema,
  onSubmit,
  children,
  isLoading = false,
  saveButtonText = 'Save',
  disableFormGrid = false,
  onDirtyChange,
  enableReinitialize = true,
  isExternallyDirty = false
}: CollapsibleSectionFormProps<T>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  return (
    <Card className="w-full overflow-hidden rounded-lg border border-base-sidebarBorder bg-base-white shadow-none">
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group w-full"
      >
        <CardHeader className="p-0">
          <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-9 text-left transition-colors hover:bg-base-backgroundHover/50 data-[state=open]:bg-transparent">
        <div className="flex min-h-[44px] flex-1 flex-col gap-1">
          <h4 className="font-sans text-xl font-semibold leading-7 tracking-[-0.4px] text-base-foreground">
            {title}
          </h4>
          {description && (
            <p className="font-sans text-base font-normal leading-6 text-base-mutedForeground">
              {description}
            </p>
          )}
        </div>
        <div className="flex h-[55px] w-[55px] shrink-0 items-center justify-center rounded-md text-base-mutedForeground transition-colors hover:bg-base-muted">
          <ChevronDown className="size-6 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent forceMount className="overflow-hidden">
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-200 ease-in-out',
              isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="overflow-hidden">
              <CardContent className="px-9 pb-9 pt-0">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={async (values, helpers) => {
                    await onSubmit(values, helpers);
                    helpers.resetForm({ values });
                  }}
                  enableReinitialize={enableReinitialize}
                >
                  {({ handleSubmit, isSubmitting, isValid, dirty }) => {
                    const effectiveDirty = dirty || isExternallyDirty;
                    const isSaveDisabled =
                      !dirty || !isValid || isSubmitting || isLoading;
                    return (
                      <div>
                        <FormDirtySync
                          dirty={effectiveDirty}
                          onDirtyChange={onDirtyChange}
                        />
                        <div
                          className={
                            disableFormGrid
                              ? 'space-y-6'
                              : 'grid grid-cols-1 gap-x-9 gap-y-4 md:grid-cols-2'
                          }
                        >
                          {children}
                        </div>
                        <div className="mt-9 flex justify-end">
                          <Button
                            type="button"
                            variant="default"
                            onClick={() => handleSubmit()}
                            disabled={isSaveDisabled}
                            data-testid="collapsible-section-save"
                            className="h-10 rounded-md bg-base-primary px-4 py-2 font-medium text-base-primaryForeground shadow-none hover:bg-button-hover-default"
                          >
                            {isSubmitting || isLoading ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {saveButtonText}
                              </>
                            ) : (
                              saveButtonText
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  }}
                </Formik>
              </CardContent>
            </div>
          </div>
        </CollapsibleContent>
    </Collapsible>
    </Card>
  );
}
